import { computeMarketSubmitStats } from '@/enterprise/components/OfferBuilder/utils';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { ToggleOfferParams } from '@/enterprise/data/useToggleOffer';
import { EnterpriseTrackEventNames } from '@/reporting/trackEventNames';
import { ExchangeRates } from '@/shared/data/useExchangeRates';

/**
 * Builds the toggle params used to turn off participation for a single market.
 * Shared by the division row turn-off and the Offer Activity customer card deletes.
 */
export const buildTurnOffParticipationParams = (takerMarket: TakerMarket) =>
  ({
    marketUuid: takerMarket.offerConfig.marketUuid,
    supplierDivisionUuid: takerMarket.takerDivisionUuid,
    seaUuid: takerMarket.offerConfig.uuid ?? undefined,
    frequency: takerMarket.offerConfig.frequency,
    makerOrganizationUuid: takerMarket.makerOrganizationUuid,
    marketPricingType: takerMarket.marketPricingType ?? undefined,
    isManuallyAwarded: takerMarket.offerConfig.isManuallyAwarded,
    isActiveExpressAccept: takerMarket.offerConfig.isActiveExpressAccept,
  }) satisfies ToggleOfferParams;

/**
 * Picks the track event recorded when participation is turned off for a single market.
 */
export const getParticipationOffEventType = (takerMarket: TakerMarket): EnterpriseTrackEventNames => {
  if (takerMarket.offerConfig.isActiveExpressAccept) {
    return 'participation::express-accept::disabled';
  }

  if (takerMarket.offerConfig.isManuallyAwarded) {
    return 'participation::manually-awarded::disabled';
  }

  return takerMarket.marketPricingType === 'BENCHMARK'
    ? 'participation::variable-rate::submitted'
    : takerMarket.marketPricingType === 'STATIC'
    ? 'participation::fixed-rate::submitted'
    : 'participation::submitted';
};

/**
 * Builds the track event payload recorded when participation is turned off for a single market.
 * Keep the division row and customer card on this helper so their events cannot drift apart.
 */
export const buildParticipationOffEventPayload = ({
  takerMarket,
  submitValues,
  exchangeRates,
}: {
  takerMarket: TakerMarket;
  submitValues: ToggleOfferParams;
  exchangeRates: ExchangeRates;
}) => {
  const marketStats = computeMarketSubmitStats({
    takerMarket,
    rate: 0,
    rateType: takerMarket.offerConfig.isDiscountBidding ? 'DISC' : 'APR',
    exchangeRates,
  });

  return {
    ...submitValues,
    offerConfig: {
      ...takerMarket.offerConfig,
      isEnabled: false,
    },
    awardedInvoiceAmount: takerMarket.offerConfig.isManuallyAwarded ? takerMarket.awardedInvoiceAmount : undefined,
    isDraft: false,
    isEdit: false,
    isEnabled: false,
    isPreferred: takerMarket.offerConfig.offerType === 'PREFERRED_TERM',
    ...marketStats,
  };
};
